import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Check } from "lucide-react";
import { api } from "@/lib/api";
import { trackChalkos as track } from "@/lib/analytics";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SPORTS = [["basketball", "Basketball"], ["flag_football", "Flag football"], ["other", "Other or not sure"]];
const ROLES = [["coach", "Coach"], ["parent", "Parent"], ["player", "Player"], ["other", "Other"]];
const C = { char: "#111315", cream: "#F5F0EA", amber: "#DB9130", graphite: "#8A8D8F", white: "#F8F7F3" };
const field = "mt-1.5 w-full border-2 border-[#8A8D8F]/50 bg-[#F8F7F3] px-3 py-3 text-[#111315] outline-none focus:border-[#DB9130]";
const label = "block font-['IBM_Plex_Mono'] text-[11px] uppercase tracking-[0.14em] text-[#8A8D8F]";

function Pills({ name, options, value, onChange }) {
  return (
    <div className="mt-2 flex flex-wrap gap-2" role="radiogroup">
      {options.map(([v, l]) => (
        <button key={v} type="button" role="radio" aria-checked={value === v} onClick={() => onChange(v)} data-testid={`rookie-${name}-${v}`}
          className={`px-3.5 py-2.5 text-sm border-2 transition-colors ${value === v ? "border-[#DB9130] bg-[#DB9130] text-[#111315] font-bold" : "border-[#8A8D8F]/50 text-[#F8F7F3] hover:border-[#F8F7F3]"}`}>
          {l}
        </button>
      ))}
    </div>
  );
}

function Thanks({ done }) {
  return (
    <div className="border-2 border-[#DB9130] p-6 sm:p-8" data-testid="rookie-thanks">
      <span className={label} style={{ color: C.amber }}>You&apos;re in</span>
      <h3 className="mt-2 font-['Anton'] uppercase text-3xl text-[#F8F7F3] leading-none">Your guide is ready.</h3>
      <p className="mt-3 text-sm text-[#F5F0EA]/80">{done.edition} edition. Check your inbox too.</p>
      <a href={done.download_url} onClick={() => track("rookie_guide_download", { edition: done.edition })} data-testid="rookie-download-btn"
        className="mt-6 inline-flex items-center gap-2 bg-[#DB9130] text-[#111315] font-['Anton'] uppercase tracking-wide text-lg px-7 py-3.5 hover:bg-[#F8F7F3] transition-colors">
        <Download size={18} /> Download
      </a>
    </div>
  );
}

function RookieForm({ source }) {
  const [f, setF] = useState({ first_name: "", email: "", sport: "", role: "", newsletter_opt_in: false, website: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const email = f.email.trim().toLowerCase();
    if (!EMAIL_RE.test(email)) { setErr("Enter a valid email address."); return; }
    if (!f.sport) { setErr("Pick a sport so we send the right edition."); return; }
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/rookie/lead", { ...f, email, first_name: f.first_name.trim() || null, role: f.role || null, source });
      track("rookie_guide_submit", { sport: f.sport, role: f.role || "unknown", source, newsletter_opt_in: f.newsletter_opt_in });
      setDone(data);
    } catch (ex) {
      setErr(ex?.response?.status === 429 ? "Too many tries. Give it a few minutes." : "Something went wrong. Try again.");
    } finally { setBusy(false); }
  };

  if (done?.download_url) return <Thanks done={done} />;
  return (
    <form onSubmit={submit} noValidate className="space-y-5" data-testid="rookie-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block"><span className={label}>First name (optional)</span>
          <input value={f.first_name} onChange={(e) => set("first_name")(e.target.value)} maxLength={60} autoComplete="given-name" className={field} data-testid="rookie-first-name" />
        </label>
        <label className="block"><span className={label}>Email</span>
          <input type="email" required value={f.email} onChange={(e) => set("email")(e.target.value)} autoComplete="email" className={field} data-testid="rookie-email" />
        </label>
      </div>
      <div><span className={label}>Sport</span><Pills name="sport" options={SPORTS} value={f.sport} onChange={set("sport")} /></div>
      <div><span className={label}>I am a</span><Pills name="role" options={ROLES} value={f.role} onChange={set("role")} /></div>
      <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" value={f.website}
        onChange={(e) => set("website")(e.target.value)} className="absolute -left-[9999px] w-px h-px opacity-0" data-testid="rookie-hp" />
      <label className="flex items-start gap-3 cursor-pointer text-sm text-[#F5F0EA]/90">
        <input type="checkbox" checked={f.newsletter_opt_in} onChange={(e) => set("newsletter_opt_in")(e.target.checked)}
          className="mt-0.5 h-5 w-5 accent-[#DB9130] shrink-0" data-testid="rookie-sideline-optin" />
        Also send me The Sideline, the weekly VoluntoldHQ email.
      </label>
      {err && <p className="text-sm text-[#F0A08A]" data-testid="rookie-error">{err}</p>}
      <button type="submit" disabled={busy} data-testid="rookie-submit"
        className="w-full bg-[#DB9130] text-[#111315] font-['Anton'] uppercase tracking-wide text-xl px-7 py-4 hover:bg-[#F8F7F3] transition-colors disabled:opacity-60">
        {busy ? "Sending..." : "Send me the guide"}
      </button>
      <p className="text-xs text-[#8A8D8F]">No spam. Unsubscribe anytime. <Link to="/legal/privacy" className="underline underline-offset-2 hover:text-[#F8F7F3]" data-testid="rookie-privacy-link">Privacy</Link></p>
    </form>
  );
}

export function RookieOffer({ source = "rookie_page", as: H = "h2" }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { track("rookie_guide_view", { source }); io.disconnect(); } }, { threshold: 0.3 });
    io.observe(el);
    return () => io.disconnect();
  }, [source]);
  return (
    <section ref={ref} className="bg-[#111315] text-[#F8F7F3] vt-grain" data-testid={`rookie-offer-${source}`}>
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14 sm:py-20 grid lg:grid-cols-[0.8fr_1.2fr] gap-10 lg:gap-16 items-center">
        <div className="mx-auto w-[62%] max-w-[260px] sm:max-w-[300px] lg:w-full lg:max-w-none">
          <img src="/rookie/cover-800.webp" srcSet="/rookie/cover-400.webp 400w, /rookie/cover-800.webp 800w"
            sizes="(min-width: 1024px) 380px, 62vw" width="1275" height="1650" loading="lazy" decoding="async"
            alt="The VoluntoldHQ Rookie Coach Guide cover" className="w-full h-auto border-2 border-[#F8F7F3]/15 shadow-[12px_12px_0_#DB9130]" data-testid="rookie-cover" />
        </div>
        <div>
          <span className="font-['IBM_Plex_Mono'] text-xs uppercase tracking-[0.18em] text-[#DB9130]" data-testid="rookie-eyebrow">Free for rookie coaches</span>
          <H className="mt-3 font-['Anton'] uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95] text-[#F8F7F3]" data-testid="rookie-headline">You got voluntold. Start here.</H>
          <p className="mt-4 text-base sm:text-lg text-[#F5F0EA]/85 max-w-xl" data-testid="rookie-sub">
            The 15-minute guide to your first season: practice plan, parent meeting script, safety basics, and the rules that come up in week 1.
          </p>
          <div className="mt-8 relative"><RookieForm source={source} /></div>
        </div>
      </div>
    </section>
  );
}

export const RookieChecks = () => (
  <ul className="max-w-6xl mx-auto px-5 sm:px-8 py-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="rookie-inside">
    {["Your first practice plan", "Parent meeting script", "Safety basics", "Week 1 rules"].map((t) => (
      <li key={t} className="flex gap-2 items-center text-[#111315] font-bold"><Check size={18} className="text-[#DB9130]" />{t}</li>
    ))}
  </ul>
);
